/**
 * Password-protected popup gating CPs/PDPs with the `custom.password_protected`
 * metafield. Unlock state is persisted in localStorage so visitors only need to
 * enter the password once per browser.
 */
(function () {
  const STORAGE_KEY = 'rr_password_protection_unlocked';

  // Snapshot of the original element so we can re-insert it if a user removes
  // it via dev tools. Captured the first time the element connects.
  let snapshotHTML = null;
  let watchdogTimer = null;
  let unlocked = false;

  function startWatchdog() {
    if (watchdogTimer || !snapshotHTML) return;
    watchdogTimer = setInterval(() => {
      if (unlocked) {
        clearInterval(watchdogTimer);
        watchdogTimer = null;
        return;
      }
      if (!document.querySelector('password-protection')) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = snapshotHTML;
        const restored = wrapper.firstElementChild;
        if (restored) document.body.appendChild(restored);
      }
    }, 1000);
  }

  async function sha256Hex(input) {
    const buffer = new TextEncoder().encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  class PasswordProtection extends HTMLElement {
    connectedCallback() {
      this.expectedHash = (this.dataset.passwordHash || '').toLowerCase();

      if (this.isUnlocked()) {
        unlocked = true;
        this.remove();
        return;
      }

      if (snapshotHTML === null) snapshotHTML = this.outerHTML;

      this.input = this.querySelector('.password-protection__input');
      this.submit = this.querySelector('.password-protection__submit');
      this.error = this.querySelector('.password-protection__error');
      this.form = this.querySelector('.password-protection__form');

      this.input.addEventListener('input', () => this.handleInput());
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));

      this.show();
      startWatchdog();
    }

    isUnlocked() {
      try {
        return localStorage.getItem(STORAGE_KEY) === this.expectedHash;
      } catch (_e) {
        return false;
      }
    }

    show() {
      this.hidden = false;
      document.documentElement.classList.add('password-protection-active');
      // Move focus into the dialog without scrolling the page.
      requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
    }

    hide() {
      unlocked = true;
      document.documentElement.classList.remove('password-protection-active');
      this.remove();
    }

    handleInput() {
      this.submit.disabled = this.input.value.trim().length === 0;
      this.error.hidden = true;
      this.input.classList.remove('password-protection__input--error');
    }

    async handleSubmit(event) {
      event.preventDefault();
      const value = this.input.value.trim();
      if (!value) return;

      const hash = await sha256Hex(value);
      if (hash === this.expectedHash) {
        try {
          localStorage.setItem(STORAGE_KEY, this.expectedHash);
        } catch (_e) {}
        this.hide();
      } else {
        this.error.hidden = false;
        this.input.classList.add('password-protection__input--error');
        this.input.select();
      }
    }
  }

  if (!customElements.get('password-protection')) {
    customElements.define('password-protection', PasswordProtection);
  }
})();
